import { serialize } from "object-to-formdata";

import { createCurrentDatetime, startOfMinute, toBackendDate } from "@tsp-core/datetime/utils";

import { AttachmentType } from "@tsp-features/form/components/attachment-field";

import { CostAllocationPeriod } from "@tsp-domains/cost-allocation/enums";
import { DepartmentUuid } from "@tsp-domains/departments/types";
import { StorableMoney } from "@tsp-domains/money/types";
import { transformToStorableMoney } from "@tsp-domains/money/utils";
import { PositionUuid } from "@tsp-domains/positions/types";
import { PropertyUuid } from "@tsp-domains/projects-portfolio/types";
import { workItemsApi } from "@tsp-domains/work-items/api";
import { WorkTypeId } from "@tsp-domains/work-types/types";

import { CreateCustomWorkPayload, CustomWorkUuid, UpdateCustomWorkPayload } from "../types";

type CreateCustomWorkRequestParams = {
  paidAt: number;
  completedAt: number;
  costAllocationPeriodType: CostAllocationPeriod;
  contractUuid: string;
  workSubTypeUuid: string;
  departmentUuid: DepartmentUuid;
  positionUuid: PositionUuid;
  propertiesBreakdown: Array<{
    propertyUuid: PropertyUuid | null;
    amount: StorableMoney;
  }>;
  attachment?: File;
  description: string;
  sign: boolean;
};

type UpdateCustomWorkRequestParams = {
  uuid: CustomWorkUuid;
  paidAt: number;
  completedAt: number;
  costAllocationPeriodType: CostAllocationPeriod;
  contractUuid: string;
  workSubTypeUuid: string;
  departmentUuid: DepartmentUuid;
  positionUuid: PositionUuid;
  property: string;
  description: string;
  attachment?: File;
  deleteAttachment?: number;
  totalAmountStorable: number;
  sign: boolean;
};

export type DeleteCustomWorkRequestParams = {
  uuid: CustomWorkUuid;
};

type GetExternalCwTypesResponseData = {
  workTypeIds: Array<WorkTypeId>;
};

export const customWorksApi = workItemsApi.injectEndpoints({
  endpoints: (builder) => ({
    getExternalCwTypes: builder.query<Array<WorkTypeId>, void>({
      query: () => ({
        url: "/getExternalCWTypes",
        method: "GET",
      }),
      transformResponse: (response: GetExternalCwTypesResponseData) => response.workTypeIds,
    }),
    createCustomWork: builder.mutation<void, CreateCustomWorkPayload>({
      query: (payload) => {
        const currentDate = toBackendDate(startOfMinute(createCurrentDatetime()));

        const data: CreateCustomWorkRequestParams = {
          paidAt: currentDate,
          completedAt: toBackendDate(startOfMinute(payload.completedAt)),
          costAllocationPeriodType: payload.costAllocationPeriod,
          contractUuid: payload.contractUuid,
          workSubTypeUuid: payload.workSubTypeUuid,
          departmentUuid: payload.departmentUuid,
          positionUuid: payload.positionUuid,
          propertiesBreakdown: payload.breakdowns.map((breakdown) => {
            return {
              propertyUuid: breakdown.propertyUuid,
              amount: transformToStorableMoney(breakdown.amount),
            };
          }),
          description: payload.description || "",
          sign: payload.isSign,
        };

        const body = serialize(data, { indices: true });

        if (payload.attachment) {
          body.set("attachment", payload.attachment.file);
        }

        return {
          url: "/createCustomWork",
          method: "POST",
          body,
        };
      },
      invalidatesTags: ["WorkItemsRecordsListing"],
    }),
    updateCustomWork: builder.mutation<void, UpdateCustomWorkPayload>({
      query: (payload) => {
        const currentDate = toBackendDate(startOfMinute(createCurrentDatetime()));

        const data: UpdateCustomWorkRequestParams = {
          uuid: payload.uuid,
          paidAt: currentDate,
          completedAt: toBackendDate(startOfMinute(payload.completedAt)),
          costAllocationPeriodType: payload.costAllocationPeriod,
          contractUuid: payload.contractUuid,
          workSubTypeUuid: payload.workSubTypeUuid,
          departmentUuid: payload.departmentUuid,
          positionUuid: payload.positionUuid,
          property: payload.propertyUuid,
          totalAmountStorable: transformToStorableMoney(payload.amount),
          description: payload.description || "",
          sign: payload.isSign,
        };

        const body = serialize(data);

        if (payload.attachment?.type === AttachmentType.File) {
          body.append("attachment", payload.attachment.file);
        } else if (!payload.attachment) {
          body.append("deleteAttachment", "1");
        }

        return {
          url: "/updateCustomWork",
          method: "POST",
          body,
        };
      },
      invalidatesTags: ["WorkItemsRecordsListing", "WorkItemDetailsRecord"],
    }),
    deleteCustomWork: builder.mutation<void, CustomWorkUuid>({
      query: (uuid) => {
        const body: DeleteCustomWorkRequestParams = {
          uuid,
        };

        return {
          url: "/deleteCustomWork",
          method: "POST",
          body,
        };
      },
      invalidatesTags: ["WorkItemsRecordsListing", "WorkItemDetailsRecord"],
    }),
  }),
});
